import { all, call, fork, put, select, takeLatest, throttle } from 'redux-saga/effects';

import * as routeActions from 'actions/Routes';
import { getBusLocationsSuccess } from 'actions/Buses';
import {
  showErrorNotification,
  showSuccessNotification,
  showInfoNotification,
  updateQuery,
} from 'actions/App';

import * as routesApi from 'api/Routes';
import { getCampuses } from 'api/Absences';

import { removeQueryParameter, setQueryParameter } from 'util/query';

import { updateRouteRelationsSuccess, updateRouteRelationsFailure } from 'actions/Modal';

import { map, getOr } from 'lodash/fp';

import moment from 'moment';

import {
  PAGE_NUMBER_QUERY_KEY,
  ROUTE_ID_QUERY,
  HISTORY_FILTERS,
  ROUTE_SELECT_CAMPUS_ITEM,
  SCHEDULE_SERVER_TIME_FORMAT,
  DATE_FORMAT,
  UNKNOWN_CAMPUS_ID,
} from 'constants/Values';

import { extractBusLocations } from 'util/BusLocationsUtils';

import {
  CREATE_NEW_ROUTE,
  CREATE_NEW_ROUTE_STATION,
  DELETE_ROUTE,
  DELETE_ROUTE_STATION,
  EDIT_ROUTE,
  GET_ACTIVE_ROUTE_DETAILS,
  GET_INACTIVE_ROUTE_DETAILS,
  GET_ACTIVE_ROUTES_HISTORY,
  GET_BUS_ROUTES,
  GET_DETAILED_ACTIVE_ROUTE_HISTORY,
  GET_DRIVER_ROUTES,
  GET_ROUTE_BY_ID,
  GET_ROUTE_CARRIERS_RELATIONS,
  GET_ROUTE_HISTORY_STATIONS_STATS,
  GET_ROUTE_HISTORY_STUDENTS_STATS,
  GET_TODAY_ROUTES,
  GET_TODAY_ROUTES_THROTTLE,
  REFLECT_STATIONS,
  REMOVE_STATIONS_BY_DIRECTION,
  SEARCH_ACTIVE_ROUTE_DELAYS_HISTORY,
  SEARCH_ACTIVE_ROUTE_LOCATIONS_HISTORY,
  SEARCH_ACTIVE_ROUTE_STATIONS_HISTORY,
  SEARCH_ACTIVE_ROUTE_STUDENTS_HISTORY,
  SEARCH_ROUTES,
  SET_COMMON_ROUTE_SCHEDULE,
  UPDATE_ROUTE_RELATIONS,
  UPDATE_STATION,
  UPDATE_STATIONS_SCHEDULE,
  UPDATE_STATION_ORDER,
  GET_ACTIVE_ROUTE_DETAILS_INFO,
  GET_CAMPUSES_FOR_ROUTES,
  COPY_ROUTE,
  GET_LOG_ROUTE_DATA,
  GET_ROUTE_STOP_DATA,
  TERMINATE_ACTIVE_ROUTE_CONFIRM,
  ACTIVE_ROUTE_LOCATION_HISTORY,
  FETCH_ALL_ROUTES_PAGINATED,
} from 'constants/ActionTypes';

import { history } from '../store';

const storedRoutesSelector = state => state.routes.listOfRoutes;
const routeDetailsSelector = state => state.routes.routeFetchedById;
const recentlyAddedRoutesSelector = state => state.routes.recentlyAddedRoutes;

const combineRoutesAndRemoveDuplicates = (existingRoutes = [], newRoutes = []) => {
  const index = existingRoutes.findIndex(route =>
    newRoutes.find(newRoute => newRoute.id === route.id),
  ); // eslint-disable-line
  const combinedRoutes = [...existingRoutes, ...newRoutes];

  if (index > -1) {
    combinedRoutes.splice(index, 1);
  }

  return combinedRoutes;
};

/**
 * Function created to update validity of updated route
 * @param updatedRoute
 * @returns {IterableIterator<*>}
 */
function* syncUpdatedRouteValidity(updatedRoute) {
  const storedRoutes = yield select(storedRoutesSelector);

  if (storedRoutes && storedRoutes.length) {
    let shouldUpdateRoutes;
    const routesToUpdate = storedRoutes.map(storedRoute => {
      if (storedRoute.id === updatedRoute.id && storedRoute.valid !== updatedRoute.valid) {
        shouldUpdateRoutes = true;

        return {
          ...storedRoute,
          valid: updatedRoute.valid,
        };
      }

      return storedRoute;
    });

    if (shouldUpdateRoutes) {
      yield put(routeActions.updateExistingSearchRoutes(routesToUpdate));
    }
  }
}

function* getRouteById({ payload }) {
  try {
    const response = yield call(routesApi.getRouteById, payload.id);
    yield put(routeActions.getRouteByIdSuccess(response.data));
    yield call(syncUpdatedRouteValidity, response.data);
  } catch (error) {
    yield put(routeActions.getRouteByIdFailure(error));
    yield put(showErrorNotification(error.messages));
  }
}

function* createStation({ payload }) {
  console.log('createStation payload: ', payload);
  try {
    yield call(
      routesApi.createNewRouteStation,
      payload.station,
      payload.routeId,
      payload.direction,
    );
    yield put(routeActions.getRouteById(payload.routeId));
    yield put(routeActions.updateStationSuccess());
    if (payload.completionHook) {
      payload.completionHook();
    }
  } catch (error) {
    yield put(routeActions.updateStationFailure());
    yield put(showErrorNotification(error.messages));
    if (payload.completionHook) {
      payload.completionHook(error);
    }
  }
}

function* deleteRouteStation({ payload }) {
  yield put(routeActions.showRouteStationsLoader());
  console.log('deleteRouteStation payload: ', payload);
  try {
    yield call(routesApi.deleteRouteStation, payload.routeId, payload.stationId, payload.direction);
    yield put(routeActions.getRouteById(payload.routeId));
    yield put(routeActions.updateStationSuccess());
    if (payload.completionHook) {
      payload.completionHook();
    }
  } catch (error) {
    yield put(routeActions.updateStationFailure());
    yield put(showErrorNotification(error.messages));
    if (payload.completionHook) {
      payload.completionHook(error);
    }
  } finally {
    yield put(routeActions.hideRouteStationsLoader());
  }
}

let cachedResponse = null;
function* getRoutes() {
  if (cachedResponse) {
    yield put(routeActions.getTodayRoutesSuccess(cachedResponse));
  }

  try {
    const response = yield call(routesApi.getTodayRoutes);
    cachedResponse = response.data.map(campus => {
      if (!campus.campusName) {
        return {
          routes: campus.routes.map(route => ({
            ...route,
            campus: UNKNOWN_CAMPUS_ID,
          })),
          campusName: UNKNOWN_CAMPUS_ID,
        };
      }
      return campus;
    });
    yield put(routeActions.getTodayRoutesSuccess(cachedResponse));
    yield put(getBusLocationsSuccess(extractBusLocations(cachedResponse)));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
    yield put(routeActions.setRoutesLoading());
  }
}

function* deleteRoute({ payload }) {
  yield put(routeActions.setRoutesLoading(true));

  try {
    yield call(routesApi.deleteRoute, payload.routeId);
    yield put(routeActions.deleteRouteSuccess(payload.routeId));

    // we should remove from store route if we currently view it
    const routeDetails = yield select(routeDetailsSelector);
    if (routeDetails && routeDetails.id === payload.routeId) {
      yield put(routeActions.getRouteByIdSuccess(null));
    }

    removeQueryParameter(ROUTE_ID_QUERY);

    if (payload.urlToRedirect) {
      yield call(history.push, payload.urlToRedirect);
    } else {
      yield call(history.push, '/routes/manage');
      yield call(getRoutes);
    }
    yield put(showSuccessNotification('pages.manageRoutes.route.deleted'));
    yield put(routeActions.routeModalSubmitedSuccess());
  } catch (error) {
    yield put(routeActions.routeModalSubmitedFailure());
    yield put(showErrorNotification(error.messages));
  } finally {
    yield put(routeActions.setRoutesLoading());
  }
}

function* getActiveRouteDetails({ payload }) {
  try {
    const { data } = yield call(routesApi.getActiveRouteDetails, payload.routeId);
    yield put(routeActions.getActiveRouteDetailsSuccess(data));
  } catch (error) {
    yield put(routeActions.getActiveRouteDetailsFailed());
    yield put(showErrorNotification(error.messages));
    yield put(routeActions.setRoutesLoading());
  }
}

function* terminateActiveRoute({ payload }) {
  try {
    const { data } = yield call(
      routesApi.terminateActiveRoute,
      payload.routeId,
      payload.completed,
    );
    yield put(routeActions.terminateActiveRouteSuccess(data));
  } catch (error) {
    yield put(routeActions.terminateActiveRouteFailed());
    yield put(showErrorNotification(error.messages));
  }
}

function* getInactiveRouteDetails({ payload }) {
  try {
    const { data } = yield call(routesApi.getInactiveRouteDetails, payload.routeId);
    yield put(routeActions.getInactiveRouteDetailsSuccess(data));
  } catch (error) {
    yield put(routeActions.getInactiveRouteDetailsFailed());
    yield put(showErrorNotification(error.messages));
    yield put(routeActions.setRoutesLoading());
  }
}

function* getActiveRouteDetailsInfo({ payload }) {
  try {
    const { data } = yield call(routesApi.getActiveRouteDetailsInfo, payload.routeId);

    yield put(routeActions.getActiveRouteDetailsInfoSuccess(data));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
    yield put(routeActions.setRoutesLoading());
  }
}

function* getDetailedActiveRouteHistory({ payload }) {
  yield put(routeActions.setRouteHistoryDetailsLoader(true));

  try {
    const { data } = yield call(routesApi.getDetailedActiveRouteHistory, payload.routeId);
    yield put(routeActions.getDetailedActiveRouteHistorySuccess(data));
  } catch (error) {
    yield put(routeActions.getDetailedActiveRouteHistoryFailure());
  } finally {
    yield put(routeActions.setRouteHistoryDetailsLoader(false));
  }
}

function* getRouteHistoryStationsStats({ payload }) {
  yield put(routeActions.setRouteHistoryStationsLoader(true));

  try {
    const { data } = yield call(routesApi.getActiveRouteStationsStatistic, payload.routeId);
    yield put(routeActions.getRouteHistoryStationsStatsSuccess(data.stationsStatistic));
  } catch (error) {
    yield put(routeActions.getRouteHistoryStationsStatsFailure());
  } finally {
    yield put(routeActions.setRouteHistoryStationsLoader(false));
  }
}

function* getRouteHistoryStudentsStats({ payload }) {
  yield put(routeActions.setRouteHistoryStudentsLoader(true));

  try {
    const { data } = yield call(routesApi.getActiveRouteStudentsStatistic, payload.routeId);
    yield put(routeActions.getRouteHistoryStudentsStatsSuccess(data.studentsStatistic));
  } catch (error) {
    yield put(routeActions.getRouteHistoryStudentsStatsFailure());
  } finally {
    yield put(routeActions.setRouteHistoryStudentsLoader(false));
  }
}

/*
 * Combining routes and removing duplicates is much needed due to the possibility
 * of a route with the same id appearing in a new portion of routes. This can happen
 * when new route was created, placed somewhere at already stores routes (storedRoutes),
 * so that a user could see it, but actually belongs to a next chunk of routes
*/
function* searchRoutes({ payload }) {
  yield put(routeActions.setRoutesLoading(true));
  try {
    const {
      loadAllRoutesAtOnce,
      query: { pagination },
      routeSearchTerm,
    } = payload;
    const page = loadAllRoutesAtOnce ? 1 : pagination.pageNumber;
    const perPage = loadAllRoutesAtOnce ? page * pagination.size : pagination.size;
    const query = {
      pagination: {
        pageNumber: page,
        size: perPage,
      },
      filter: routeSearchTerm,
    };

    const lastRouteSearchTerm = yield select(state => state.routes.lastRouteSearchTerm);
    const { data } = yield call(routesApi.searchRoutes, query);
    const { entities, pageCount } = data;
    const storedRoutes = yield select(storedRoutesSelector);
    const routesList =
      lastRouteSearchTerm !== routeSearchTerm
        ? entities
        : combineRoutesAndRemoveDuplicates(storedRoutes, entities);

    if (pagination.pageNumber > 1) {
      setQueryParameter(PAGE_NUMBER_QUERY_KEY, pagination.pageNumber);
    } else {
      removeQueryParameter(PAGE_NUMBER_QUERY_KEY);
    }

    yield put(routeActions.getAllRoutesSuccess(routesList, pageCount, routeSearchTerm));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  } finally {
    yield put(routeActions.setRoutesLoading());
  }
}

function* fetchAllRoutesPaginatedSaga() {
  try {
    let page = 1;
    let totalPages = 1;
    const allRoutes = [];

    do {
      const response = yield call(routesApi.searchRoutes, {
        pagination: { pageNumber: page, size: 100 },
      });
      const { entities, pageCount } = response.data;
      allRoutes.push(...entities);
      totalPages = pageCount;
      page += 1;
    } while (page <= totalPages);

    yield put(routeActions.fetchAllRoutesPaginatedSuccess(allRoutes));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  }
}

function* createRoute({ payload }) {
  yield put(routeActions.setRoutesLoading(true));

  try {
    // route is always going to be invalid at first
    const { data } = yield call(routesApi.createNewRoute, payload.route);
    const createdRoute = { ...data, valid: false, newRoute: true };
    yield put(routeActions.addNewRouteSuccess(createdRoute));
    yield put(routeActions.routeModalSubmitedSuccess());
    yield put(showSuccessNotification('pages.manageRoutes.route.created'));
  } catch (error) {
    yield put(routeActions.routeModalSubmitedFailure());
    yield put(showErrorNotification(error.messages));
  } finally {
    yield put(routeActions.setRoutesLoading(false));
  }
}

function* editRoute({ payload }) {
  yield put(routeActions.setRoutesLoading(true));

  try {
    const updatedRoute = yield call(routesApi.editRoute, payload);

    const storedRoutes = yield select(storedRoutesSelector);
    const recentlyAddedRoutes = yield select(recentlyAddedRoutesSelector);

    let requiredRoutesUpdate = false;
    if (updatedRoute && updatedRoute.data) {
      if (recentlyAddedRoutes.find(route => route.id === updatedRoute.data.id)) {
        yield put(routeActions.updateRecentlyAddedRoute(updatedRoute));
      } else if (storedRoutes && storedRoutes.length) {
        // we should update route record in our store without additional requests
        const routesToUpdate = storedRoutes.map(route => {
          if (route.id === updatedRoute.data.id) {
            requiredRoutesUpdate = true;
            return { ...updatedRoute.data, valid: route.valid };
          }

          return route;
        });

        if (requiredRoutesUpdate) {
          yield put(routeActions.updateExistingSearchRoutes(routesToUpdate));
        }
      }

      const routeDetails = yield select(routeDetailsSelector);

      // we need update route details to prevent data refetch
      if (routeDetails && routeDetails.id === updatedRoute.data.id) {
        yield put(
          routeActions.getRouteByIdSuccess({
            ...routeDetails,
            name: updatedRoute.data.name,
            number: updatedRoute.data.number,
            campusResponse: updatedRoute.data.campusResponse,
            days: updatedRoute.data.days,
          }),
        );
      }
    }

    yield put(showInfoNotification('pages.manageRoutes.route.updated'));
    yield put(routeActions.routeModalSubmitedSuccess());
  } catch (error) {
    yield put(routeActions.routeModalSubmitedFailure());
    yield put(showErrorNotification(error.messages));
  }

  yield put(routeActions.setRoutesLoading());
}

function* copyRoute({ payload }) {
  const { id, name, number, campusId } = payload;
  yield put(routeActions.setRoutesLoading(true));

  try {
    const { data } = yield call(routesApi.copyRoute, id, name, number, campusId);

    const createdRoute = { ...data, newRoute: true };
    yield put(routeActions.addNewRouteSuccess(createdRoute));
    yield put(routeActions.toggleCopyRouteModal());
    yield put(showSuccessNotification('pages.manageRoutes.route.created'));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
    yield put(routeActions.routeModalSubmitedFailure());
  }

  yield put(routeActions.setRoutesLoading(false));
}

function* reflectStationsFromDirection({ payload }) {
  yield put(routeActions.showRouteStationsLoader());

  try {
    yield call(routesApi.reflectStations, payload.routeId, payload.direction);
    yield put(routeActions.getRouteById(payload.routeId));
    yield put(routeActions.hideRouteStationsLoader());
  } catch (error) {
    yield put(showErrorNotification(error.messages));
    yield put(routeActions.hideRouteStationsLoader());
  }
}

function* getRouteCarriersRelations({ payload }) {
  try {
    const { data } = yield call(routesApi.getRouteCarriersRelations, payload.routeId);

    yield put(routeActions.getRouteCarriersRelationsSuccess(data));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  }
}

function* removeStationsByDirection({ payload }) {
  yield put(routeActions.showRouteStationsLoader());

  try {
    yield call(routesApi.removeStationsByDirection, payload.routeId, payload.direction);

    if (payload.urlToRedirect) {
      yield call(history.push, payload.urlToRedirect);
    }
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  } finally {
    yield put(routeActions.hideRouteStationsLoader());
  }
}

function* updateRouteRelations({ payload }) {
  try {
    yield call(routesApi.updateRouteRelations, payload.routeId, payload.busesAndDrivers);

    yield put(routeActions.getRouteCarriersRelations(payload.routeId));
    // route re-fetch to update route validity
    yield put(routeActions.getRouteById(payload.routeId));
    yield put(updateRouteRelationsSuccess());
  } catch (error) {
    yield put(updateRouteRelationsFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* setCommonRouteSchedule({ payload }) {
  yield put(routeActions.showRouteStationsLoader());

  try {
    yield call(routesApi.setCommonRouteSchedule, payload.routeId, payload.direction);
    yield put(routeActions.getRouteById(payload.routeId));
    yield put(routeActions.hideRouteStationsLoader());
  } catch (error) {
    yield put(showErrorNotification(error.messages));
    yield put(routeActions.hideRouteStationsLoader());
  }
}

function* getBusRoutes({ payload }) {
  yield put(routeActions.setBusRoutesLoading(true));

  try {
    const { data } = yield call(routesApi.getBusRoutes, payload.busId);

    yield put(routeActions.getBusRoutesSuccess(payload.busId, data));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  }

  yield put(routeActions.setBusRoutesLoading(false));
}

function* getDriverRoutes({ payload }) {
  yield put(routeActions.setDriverRoutesLoading(true));

  try {
    const { data } = yield call(routesApi.getDriverRoutes, payload.driverId);

    yield put(routeActions.getDriverRoutesSuccess(payload.driverId, data));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  }

  yield put(routeActions.setDriverRoutesLoading(false));
}

function* searchActiveRouteDelaysHistory({ payload }) {
  yield put(routeActions.setRouteHistoryDelaysLoader(true));

  try {
    const { data: delays } = yield call(
      routesApi.searchActiveRouteDelaysHistory,
      payload.routeId,
      payload.query,
    );

    if (payload.affectQuery) {
      yield put(updateQuery(payload.query.pagination));
    }

    yield put(
      routeActions.searchActiveRouteDelaysHistorySuccess(delays.entities, delays.pageCount),
    );
  } catch (error) {
    yield put(routeActions.searchActiveRouteDelaysHistoryFailure());
  } finally {
    yield put(routeActions.setRouteHistoryDelaysLoader(false));
  }
}

function* searchActiveRouteLocationsHistory({ payload }) {
  yield put(routeActions.setRouteHistoryLocationsLoader(true));

  try {
    const { data: locations } = yield call(
      routesApi.searchActiveRouteLocationsHistory,
      payload.routeId,
      payload.query,
    );

    if (payload.affectQuery) {
      yield put(updateQuery(payload.query.pagination));
    }

    yield put(
      routeActions.searchActiveRouteLocationsHistorySuccess(
        locations.entities,
        locations.pageCount,
      ),
    );
  } catch (error) {
    yield put(routeActions.searchActiveRouteLocationsHistoryFailure());
  } finally {
    yield put(routeActions.setRouteHistoryLocationsLoader(false));
  }
}

function* activeRouteLocationHistory({ payload }) {
  yield put(routeActions.setRouteHistoryLocationsLoader(true));

  let allLocations = [];
  let pageNumber = 1;
  let pageCount = 1; // Initialize with 1 to enter the loop

  try {
    while (pageNumber <= pageCount) {
      const query = {
        pagination: {
          pageNumber,
          size: 2 * 60 * 4, // about 2 hours worth
          sort: [
            {
              direction: 'ASC',
              property: 'requestTime',
            },
          ],
        },
      };

      const { data: locations } = yield call(
        routesApi.activeRouteLocationHistory,
        payload.routeId,
        query,
      );

      allLocations = [...allLocations, ...locations.entities];
      ({ pageCount } = locations); // Update page count from response
      pageNumber += 1; // Move to next page
    }

    yield put(routeActions.activeRouteLocationHistorySuccess(allLocations));
  } catch (error) {
    yield put(routeActions.activeRouteLocationHistoryFailure());
  } finally {
    yield put(routeActions.setRouteHistoryLocationsLoader(false));
  }
}

function* searchActiveRouteStationsHistory({ payload }) {
  yield put(routeActions.setRouteHistoryStationsLoader(true));

  try {
    const { data: stations } = yield call(
      routesApi.searchActiveRouteStationsHistory,
      payload.routeId,
      payload.query,
    );

    if (payload.fetchStats && stations.entities.length) {
      yield call(getRouteHistoryStationsStats, { payload });
    }

    if (payload.affectQuery) {
      yield put(updateQuery(payload.query.pagination));
    }

    yield put(
      routeActions.searchActiveRouteStationsHistorySuccess(stations.entities, stations.pageCount),
    );
  } catch (error) {
    yield put(routeActions.searchActiveRouteStationsHistoryFailure());
  } finally {
    yield put(routeActions.setRouteHistoryStationsLoader(false));
  }
}

function* searchActiveRouteStudentsHistory({ payload }) {
  yield put(routeActions.setRouteHistoryStudentsLoader(true));

  try {
    const { data: students } = yield call(
      routesApi.searchActiveRouteStudentsHistory,
      payload.routeId,
      payload.query,
    );

    if (payload.fetchStats && students.entities.length) {
      yield call(getRouteHistoryStudentsStats, { payload });
    }

    if (payload.affectQuery) {
      yield put(updateQuery(payload.query.pagination));
    }

    yield put(
      routeActions.searchActiveRouteStudentsHistorySuccess(students.entities, students.pageCount),
    );
  } catch (error) {
    yield put(routeActions.searchActiveRouteStudentsHistoryFailure());
  } finally {
    yield put(routeActions.setRouteHistoryStudentsLoader(false));
  }
}

function* updateStation({ payload }) {
  yield put(routeActions.showRouteStationsLoader());
  console.log('updateStation payload', payload);
  try {
    yield call(
      routesApi.updateStation,
      payload.routeId,
      payload.stationId,
      payload.direction,
      payload.station,
    );
    yield put(routeActions.getRouteById(payload.routeId));
    yield put(routeActions.updateStationSuccess());
    if (payload.completionHook) {
      payload.completionHook();
    }
  } catch (error) {
    yield put(routeActions.updateStationFailure());
    yield put(showErrorNotification(error.messages));
    if (payload.completionHook) {
      payload.completionHook(error);
    }
  } finally {
    yield put(routeActions.hideRouteStationsLoader());
  }
}

function* updateStationsSchedule({ payload }) {
  // yield put(routeActions.showRouteStationsLoader());

  try {
    const response = yield call(
      routesApi.updateStationsSchedule,
      payload.routeId,
      payload.direction,
      payload.schedule,
    );
    yield put(routeActions.getRouteByIdSuccess(response.data));
    yield put(routeActions.updateStationSuccess());
    if (payload.completionHook) {
      payload.completionHook();
    }
  } catch (error) {
    yield put(routeActions.updateStationFailure());
    yield put(showErrorNotification(error.messages));
    if (payload.completionHook) {
      payload.completionHook(error);
    }
  } finally {
    yield put(routeActions.hideRouteStationsLoader());
  }
}

function* updateStationOrder({ payload }) {
  yield put(routeActions.showRouteStationsLoader());

  try {
    yield call(
      routesApi.updateStationOrder,
      payload.routeId,
      payload.stationId,
      payload.direction,
      payload.operation,
    );
    yield put(routeActions.getRouteById(payload.routeId));
    yield put(routeActions.hideRouteStationsLoader());
  } catch (error) {
    yield put(showErrorNotification(error.messages));
    yield put(routeActions.hideRouteStationsLoader());
  }
}

function* getRoutesHistory({ payload }) {
  yield put(routeActions.showRoutesHistoryLoader());

  try {
    const { data } = yield call(routesApi.getActiveRoutesHistory, payload.query);

    if (payload.affectQuery) {
      yield put(updateQuery(payload.query.pagination, HISTORY_FILTERS, payload.params));
    }

    yield put(routeActions.getActiveRoutesHistorySuccess(data.entities, data.pageCount));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  } finally {
    yield put(routeActions.hideRoutesHistoryLoader());
  }
}

function* getCampusesForRoutes() {
  try {
    const { data } = yield call(getCampuses);
    const routesChooseCampuses = {
      id: ROUTE_SELECT_CAMPUS_ITEM.ID,
      extName: ROUTE_SELECT_CAMPUS_ITEM.EXT_NAME,
    };

    data.unshift(routesChooseCampuses);

    yield put(routeActions.getCampusesForRoutesSuccess(data));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
    yield put(routeActions.getCampusesForRoutesFailure());
  }
}

const mapLogTableData = data =>
  map(item => {
    const studentName = getOr('-', ['student', 'fullName'], item);
    const status = getOr('-', ['status'], item);
    const scheduled = getOr('-', ['stationHistory', 'planedArrivalTime'], item);
    const arrived = getOr('-', ['stationHistory', 'arrivalTime'], item);
    const deviation = getOr('-', ['stationHistory', 'deviation'], item);
    const statusTime = getOr('-', ['stationHistory', 'actionTime'], item);
    const departed = getOr('-', ['stationHistory', 'departureTime'], item);
    const tmpPlannedStopAddress = getOr('-', ['expectedStationLocation', 'globalName'], item);
    const plannedStopAddress =
      tmpPlannedStopAddress === '-' || tmpPlannedStopAddress === null
        ? []
        : tmpPlannedStopAddress.split(', ');
    const plannedStopName = getOr('-', ['expectedStationLocation', 'name'], item);
    const longitude = getOr(null, ['stationLocation', 'longitude'], item);
    const latitude = getOr(null, ['stationLocation', 'latitude'], item);
    const location = latitude && longitude ? [String(latitude), String(longitude)] : [];
    const deviationKm = getOr('-', ['deviationKm'], item);

    const planedArrivalTimeValue = moment(scheduled, SCHEDULE_SERVER_TIME_FORMAT);
    const arrivalTimeValue = moment(arrived, SCHEDULE_SERVER_TIME_FORMAT);
    const statusTimeValue = moment(statusTime, DATE_FORMAT.SERVER_DATE_TIME);
    const departureTimeValue = moment(departed, SCHEDULE_SERVER_TIME_FORMAT);
    const actualLocation = getOr(null, ['stationLocation'], item);
    const expectedLocation = getOr(null, ['expectedStationLocation'], item);

    return {
      studentName,
      status,
      planedArrivalTime: planedArrivalTimeValue.isValid()
        ? planedArrivalTimeValue.format('LT')
        : null,
      arrivalTime: arrivalTimeValue.isValid() ? arrivalTimeValue.format('LT') : null,
      deviation,
      statusTime: statusTimeValue.isValid() ? statusTimeValue.format('LT') : null,
      departureTime: departureTimeValue.isValid() ? departureTimeValue.format('LT') : null,
      plannedStopAddress,
      plannedStopName,
      location,
      deviationKm: deviationKm === null ? deviationKm : deviationKm.toFixed(2),
      mapData: {
        actualLocation,
        expectedLocation,
        deviationKm: deviationKm === null ? deviationKm : deviationKm.toFixed(2),
      },
    };
  }, data);

function* getLogRouteData({ payload }) {
  const { routeId, query } = payload;
  try {
    const { data } = yield call(routesApi.getLogHistoryData, routeId, JSON.stringify(query));
    const preparedData = mapLogTableData(data.entities);

    yield put(updateQuery(query.pagination));
    yield put(
      routeActions.getLogRouteDataSuccess({ data: preparedData, pageCount: data.pageCount }),
    );
  } catch (error) {
    yield put(routeActions.getLogRouteDataFailure());
    yield put(showErrorNotification(error.messages));
  }
}

const mapRouteStopTableData = entities =>
  map(item => {
    const schedule = getOr('-', ['planedArrivalTime'], item);
    const arrival = getOr('-', ['arrivalTime'], item);
    const departure = getOr('-', ['departureTime'], item);

    const timeScheduleValue = moment(schedule, SCHEDULE_SERVER_TIME_FORMAT);
    const timeArrivalValue = moment(arrival, SCHEDULE_SERVER_TIME_FORMAT);
    const timeDepartureValue = moment(departure, SCHEDULE_SERVER_TIME_FORMAT);

    const deviation = getOr('-', ['deviation'], item);
    const stopName = getOr('-', ['stationLocation', 'name'], item);
    const stopAdress = getOr('-', ['stationLocation', 'globalName'], item);
    const stopLocation = {
      lat: getOr('-', ['stationLocation', 'latitude'], item),
      lng: getOr('-', ['stationLocation', 'longitude'], item),
    };
    const passengerStatistics = {
      scheduledStudents: getOr('-', ['studentsStatistic', 'PLANNED'], item),
      presentStudents: getOr('-', ['studentsStatistic', 'BOARDED'], item),
      absentStudents: getOr('-', ['studentsStatistic', 'ABSENT'], item),
      misseddStudents: getOr('-', ['studentsStatistic', 'MISSED'], item),
      divergenceStudents: getOr('-', ['studentsStatistic', 'DIVERGENCE'], item),
      extraStudents: getOr('-', ['studentsStatistic', 'EXTRA'], item),
    };
    const activeRouteId = getOr('-', ['activeRouteId'], item);
    const id = `${activeRouteId || ''} ${schedule || ''} ${arrival || ''}`.trim();

    return {
      timeSchedule: timeScheduleValue.isValid() ? timeScheduleValue.format('LT') : null,
      timeArrival: timeArrivalValue.isValid() ? timeArrivalValue.format('LT') : null,
      deviation,
      timeDeparture: timeDepartureValue.isValid() ? timeDepartureValue.format('LT') : null,
      stopName,
      stopAdress,
      stopLocation,
      passengerStatistics,
      id,
    };
  }, entities);

function* getRouteStopDataSaga({ payload }) {
  const { routeId, query } = payload;
  try {
    const { data } = yield call(routesApi.getRouteStopHistoryData, routeId, JSON.stringify(query));
    const preparedData = mapRouteStopTableData(data.entities);
    yield put(updateQuery(query.pagination));
    yield put(
      routeActions.getRouteStopDataSuccess({ data: preparedData, pageCount: data.pageCount }),
    );
  } catch (error) {
    yield put(routeActions.getRouteStopDataFailure());
    yield put(showErrorNotification(error.messages));
  }
}

export function* createNewRoute() {
  yield takeLatest(CREATE_NEW_ROUTE, createRoute);
}

export function* createNewRouteStation() {
  yield takeLatest(CREATE_NEW_ROUTE_STATION, createStation);
}

export function* getActiveRouteDetailsSaga() {
  yield takeLatest(GET_ACTIVE_ROUTE_DETAILS, getActiveRouteDetails);
}

export function* confirmTerminateActiveRouteSaga() {
  yield takeLatest(TERMINATE_ACTIVE_ROUTE_CONFIRM, terminateActiveRoute);
}

export function* getInactiveRouteDetailsSaga() {
  yield takeLatest(GET_INACTIVE_ROUTE_DETAILS, getInactiveRouteDetails);
}

export function* getActiveRouteDetailsInfoSaga() {
  yield takeLatest(GET_ACTIVE_ROUTE_DETAILS_INFO, getActiveRouteDetailsInfo);
}

export function* getActiveRoutesHistory() {
  yield takeLatest(GET_ACTIVE_ROUTES_HISTORY, getRoutesHistory);
}

export function* getBusRoutesSaga() {
  yield takeLatest(GET_BUS_ROUTES, getBusRoutes);
}

export function* getDetailedActiveRouteHistorySaga() {
  yield takeLatest(GET_DETAILED_ACTIVE_ROUTE_HISTORY, getDetailedActiveRouteHistory);
}

export function* getDriverRoutesSaga() {
  yield takeLatest(GET_DRIVER_ROUTES, getDriverRoutes);
}

export function* getPaginatedRoutes() {
  yield takeLatest(SEARCH_ROUTES, searchRoutes);
}

export function* getRoute() {
  yield takeLatest(GET_ROUTE_BY_ID, getRouteById);
}

export function* getRouteCarriersRelationsSaga() {
  yield takeLatest(GET_ROUTE_CARRIERS_RELATIONS, getRouteCarriersRelations);
}

export function* getRouteHistoryStationsStatsSaga() {
  yield takeLatest(GET_ROUTE_HISTORY_STATIONS_STATS, getRouteHistoryStationsStats);
}

export function* getRouteHistoryStudentsStatsSaga() {
  yield takeLatest(GET_ROUTE_HISTORY_STUDENTS_STATS, getRouteHistoryStudentsStats);
}

export function* getTodayRoutes() {
  yield takeLatest(GET_TODAY_ROUTES, getRoutes);
}

export function* getTodayRoutesThrottle() {
  yield throttle(500, GET_TODAY_ROUTES_THROTTLE, getRoutes);
}

export function* searchActiveRouteDelaysHistorySaga() {
  yield takeLatest(SEARCH_ACTIVE_ROUTE_DELAYS_HISTORY, searchActiveRouteDelaysHistory);
}

export function* searchActiveRouteLocationsHistorySaga() {
  yield takeLatest(SEARCH_ACTIVE_ROUTE_LOCATIONS_HISTORY, searchActiveRouteLocationsHistory);
}

export function* activeRouteLocationHistorySaga() {
  yield takeLatest(ACTIVE_ROUTE_LOCATION_HISTORY, activeRouteLocationHistory);
}

export function* searchActiveRouteStationsHistorySaga() {
  yield takeLatest(SEARCH_ACTIVE_ROUTE_STATIONS_HISTORY, searchActiveRouteStationsHistory);
}

export function* searchActiveRouteStudentsHistorySaga() {
  yield takeLatest(SEARCH_ACTIVE_ROUTE_STUDENTS_HISTORY, searchActiveRouteStudentsHistory);
}

export function* updateRoute() {
  yield takeLatest(EDIT_ROUTE, editRoute);
}

export function* updateRouteRelationsSaga() {
  yield takeLatest(UPDATE_ROUTE_RELATIONS, updateRouteRelations);
}

export function* copy() {
  yield takeLatest(COPY_ROUTE, copyRoute);
}

export default function* rootSaga() {
  yield all([
    fork(createNewRoute),
    fork(createNewRouteStation),
    fork(getActiveRouteDetailsSaga),
    fork(confirmTerminateActiveRouteSaga),
    fork(getInactiveRouteDetailsSaga),
    fork(getActiveRouteDetailsInfoSaga),
    fork(getActiveRoutesHistory),
    fork(getBusRoutesSaga),
    fork(getDetailedActiveRouteHistorySaga),
    fork(getDriverRoutesSaga),
    fork(getPaginatedRoutes),
    fork(getRoute),
    fork(getRouteCarriersRelationsSaga),
    fork(getRouteHistoryStationsStatsSaga),
    fork(getRouteHistoryStudentsStatsSaga),
    fork(getTodayRoutes),
    fork(getTodayRoutesThrottle),
    fork(searchActiveRouteDelaysHistorySaga),
    fork(searchActiveRouteLocationsHistorySaga),
    fork(activeRouteLocationHistorySaga),
    fork(searchActiveRouteStationsHistorySaga),
    fork(searchActiveRouteStudentsHistorySaga),
    fork(updateRoute),
    fork(copy),
    fork(updateRouteRelationsSaga),
    takeLatest(DELETE_ROUTE, deleteRoute),
    takeLatest(DELETE_ROUTE_STATION, deleteRouteStation),
    takeLatest(REFLECT_STATIONS, reflectStationsFromDirection),
    takeLatest(REMOVE_STATIONS_BY_DIRECTION, removeStationsByDirection),
    takeLatest(SET_COMMON_ROUTE_SCHEDULE, setCommonRouteSchedule),
    takeLatest(UPDATE_STATION, updateStation),
    takeLatest(UPDATE_STATIONS_SCHEDULE, updateStationsSchedule),
    takeLatest(UPDATE_STATION_ORDER, updateStationOrder),
    takeLatest(GET_CAMPUSES_FOR_ROUTES, getCampusesForRoutes),
    takeLatest(GET_LOG_ROUTE_DATA, getLogRouteData),
    takeLatest(GET_ROUTE_STOP_DATA, getRouteStopDataSaga),
    takeLatest(FETCH_ALL_ROUTES_PAGINATED, fetchAllRoutesPaginatedSaga),
  ]);
}
